import React, { useState, useEffect } from 'react';
import { SectionGuide } from '../../components/pas/SectionGuide';
import { useNavigate } from 'react-router-dom';
import { usePASAdminAuth } from '../../context/PASAdminAuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { TxResultModal, ChainStatusCell } from './AdminTxWidgets';
import { AdminCreditLines } from '../../components/pas/AdminCreditLines';
import { AdminClientTools } from '../../components/pas/AdminClientTools';
import { AdminRedemptions } from '../../components/pas/AdminRedemptions';
import { AdminEthPurchases } from '../../components/pas/AdminEthPurchases';
import { AdminMoonPayOrders } from '../../components/pas/AdminMoonPayOrders';
import { AdminNetworkCard } from '../../components/pas/AdminNetworkCard';
import { AdminTaxForms } from '../../components/pas/AdminTaxForms';
import { AdminLedger } from '../../components/pas/AdminLedger';
import { AdminTreasuryCheck } from '../../components/pas/AdminTreasuryCheck';
import { AdminTinCell } from '../../components/pas/AdminTinCell';
import { AdminSendEthCard } from '../../components/pas/AdminSendEthCard';
import { AdminContractDeployPanel } from '../../components/pas/AdminContractDeployPanel';
import { WalletAlertsCard } from '../../components/pas/WalletAlertsCard';
import { BethMarketPanel } from '../../components/pas/BethMarketPanel';
import { IssuanceNetworkCard } from '../../components/pas/IssuanceNetworkCard';
import { BridgeEthCard } from '../../components/pas/BridgeEthCard';
import { FeeWaiverSwitch } from '../../components/pas/FeeWaiverSwitch';
import { TierWaiverSwitches } from '../../components/pas/TierWaiverSwitches';
import { ActAsButton } from '../../components/pas/ActAs';
import { AdminActLog } from '../../components/pas/AdminActLog';
import { WeeklyLimitCell } from '../../components/pas/WeeklyLimitCell';
import { SubscriberGasCard } from '../../components/pas/SubscriberGasCard';
import { AnalyticsDashboard } from '../../components/pas/AnalyticsDashboard';
import { VideoGeneratorPanel } from '../../components/pas/VideoGeneratorPanel';
import { FacebookSharePanel } from '../../components/pas/FacebookSharePanel';
import { AdminSwapPanel } from '../../components/pas/AdminSwapPanel';
import { AdminDeskPanel } from '../../components/pas/AdminDeskPanel';
import { CreditCashoutAdmin } from '../../components/pas/CreditCashoutAdmin';
import { EarningsDashboard } from '../../components/pas/EarningsDashboard';
import { AdminBankPanel } from '../../components/pas/AdminBankPanel';
import { BankPartnershipPanel } from '../../components/pas/BankPartnershipPanel';
import { BankConnectivityHub } from '../../components/pas/BankConnectivityHub';
import { HedgeFundAdmin } from '../../components/pas/HedgeFundAdmin';
import { SwapReadinessCard } from '../../components/pas/SwapReadinessCard';
import { CardBuysAdmin } from '../../components/pas/CardBuysAdmin';
import { AnnounceUpdateCard } from '../../components/pas/AnnounceUpdateCard';
import { ArrowLeftRight, Landmark, PiggyBank, Handshake, Crown, CreditCard } from 'lucide-react';
import { 
  Shield, Users, Wallet, FileCheck, History, LogOut, Plus, Send,
  Coins, DollarSign, ExternalLink, RefreshCw, Search, ArrowUpRight,
  QrCode, Eye, AlertCircle, CheckCircle, Copy, Check, Fuel, FileCode,
  ArrowRightLeft, BarChart3, Film
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PASAdminDashboard = () => {
  const navigate = useNavigate();
  const { pasAdmin, token, logout, loading: authLoading } = usePASAdminAuth();
  const [activeTab, setActiveTab] = useState(new URLSearchParams(window.location.search).get('tab') || 'dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [clients, setClients] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Credit modal
  const [showCredit, setShowCredit] = useState(false);
  const [creditData, setCreditData] = useState({ client_id: '', beth_amount: '', memo: '' });
  const [crediting, setCrediting] = useState(false);

  // Transfer modal
  const [showTransfer, setShowTransfer] = useState(false);
  const [txResult, setTxResult] = useState(null);
  const [toolsClient, setToolsClient] = useState(null);
  const [transferData, setTransferData] = useState({ 
    from_client_id: '',
    to_client_id: '', 
    to_external_address: '', 
    beth_amount: '', 
    memo: '',
    transfer_type: 'internal'
  });
  const [transferring, setTransferring] = useState(false);

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!authLoading && !pasAdmin) {
      navigate('/pas/admin/login');
    }
  }, [authLoading, pasAdmin, navigate]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, clientsRes, txRes, checksRes] = await Promise.all([
        axios.get(`${API}/pas-admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/pas-admin/clients?limit=100`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/pas-admin/transactions?limit=100`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/pas-admin/checks?limit=100`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setDashboard(dashRes.data);
      setClients(clientsRes.data.clients || []);
      setTransactions(txRes.data.transactions || []);
      setChecks(checksRes.data.checks || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCredit = async (e) => {
    e.preventDefault();
    setCrediting(true);
    try {
      const response = await axios.post(
        `${API}/pas-admin/credit`,
        {
          client_id: creditData.client_id,
          beth_amount: parseFloat(creditData.beth_amount),
          memo: creditData.memo || 'Admin credit',
          register_on_chain: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowCredit(false);
      setCreditData({ client_id: '', beth_amount: '', memo: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Credit failed');
    } finally {
      setCrediting(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    setTransferring(true);
    try {
      const payload = {
        from_client_id: transferData.from_client_id || null,
        beth_amount: parseFloat(transferData.beth_amount),
        memo: transferData.memo
      };
      if (transferData.transfer_type === 'internal') {
        payload.to_client_id = transferData.to_client_id;
      } else {
        payload.to_external_address = transferData.to_external_address;
      }

      const response = await axios.post(`${API}/pas-admin/transfer`, payload, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      toast.dismiss();
      setTxResult(response.data);
      setShowTransfer(false);
      setTransferData({ from_client_id: '', to_client_id: '', to_external_address: '', beth_amount: '', memo: '', transfer_type: 'internal' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const filteredClients = clients.filter(c => 
    c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.wallet_address?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (authLoading || !pasAdmin) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-purple-500/30">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">BETHCOIN ADMIN</h1>
              <p className="text-xs text-purple-400">Superadmin Access</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm">{pasAdmin.email}</span>
            <button
              onClick={() => { logout(); navigate('/'); }}
              data-testid="admin-logout-btn"
              className="p-2 text-slate-400 hover:text-white transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Master Wallet Info */}
        <div className="bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl p-6 mb-8 text-white">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <p className="text-purple-200 text-sm mb-1">Master Wallet (Unlimited Access)</p>
              <div className="flex items-center gap-2 mb-2">
                <code className="text-lg font-mono">{dashboard?.master_wallet?.address}</code>
                <button 
                  onClick={() => copyToClipboard(dashboard?.master_wallet?.address)}
                  className="p-1 hover:bg-white/20 rounded"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-sm text-purple-100 mb-2" data-testid="master-onchain-balance">
                {dashboard?.master_wallet?.on_chain?.connected
                  ? `On-chain: ${dashboard.master_wallet.on_chain.beth?.toLocaleString()} BETH · ${dashboard.master_wallet.on_chain.eth?.toFixed(4)} ETH for fees on Sepolia`
                  : 'Blockchain unreachable right now'}
              </p>
              <a 
                href={dashboard?.master_wallet?.etherscan_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-purple-200 hover:text-white text-sm flex items-center gap-1"
              >
                View on Etherscan <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowCredit(true)}
                data-testid="admin-credit-btn"
                className="px-6 py-3 bg-white text-purple-700 rounded-xl font-semibold flex items-center gap-2 hover:bg-purple-50 transition-colors"
              >
                <Plus className="w-5 h-5" /> Credit Client
              </button>
              <button
                onClick={() => setShowTransfer(true)}
                data-testid="admin-transfer-btn"
                className="px-6 py-3 bg-white/20 text-white rounded-xl font-semibold flex items-center gap-2 hover:bg-white/30 transition-colors"
              >
                <Send className="w-5 h-5" /> Transfer BETH
              </button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total Clients</p>
                <p className="text-xl font-bold text-white">{dashboard?.clients?.total || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <Coins className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">BETH Circulation</p>
                <p className="text-xl font-bold text-white">{dashboard?.beth?.total_circulation?.toLocaleString() || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <History className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Transactions</p>
                <p className="text-xl font-bold text-white">{dashboard?.transactions?.total || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Active Checks</p>
                <p className="text-xl font-bold text-white">{dashboard?.checks?.active || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-4 mb-6 border-b border-slate-700 pb-4">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: <Shield className="w-5 h-5" /> },
            { id: 'analytics', label: 'Analytics', icon: <BarChart3 className="w-5 h-5" /> },
            { id: 'videos', label: 'Videos', icon: <Film className="w-5 h-5" /> },
            { id: 'bank', label: 'Bank (Treasury Prime)', icon: <Landmark className="w-5 h-5" /> },
            { id: 'partnerships', label: 'Bank Partnerships', icon: <Handshake className="w-5 h-5" /> },
            { id: 'hedge-fund', label: 'Hedge Fund (Tier 2)', icon: <Crown className="w-5 h-5" /> },
            { id: 'card-buys', label: 'Card Buys', icon: <CreditCard className="w-5 h-5" /> },
            { id: 'earnings', label: 'Earnings', icon: <PiggyBank className="w-5 h-5" /> },
            { id: 'desk', label: 'Mint & Redeem Desk', icon: <Landmark className="w-5 h-5" /> },
            { id: 'swaps', label: 'Exchange & Swap', icon: <ArrowLeftRight className="w-5 h-5" /> },
            { id: 'clients', label: 'Clients', icon: <Users className="w-5 h-5" /> },
            { id: 'transactions', label: 'Transactions', icon: <History className="w-5 h-5" /> },
            { id: 'checks', label: 'Checks', icon: <FileCheck className="w-5 h-5" /> },
            { id: 'credit-lines', label: 'Credit Lines', icon: <DollarSign className="w-5 h-5" /> },
            { id: 'redemptions', label: 'Cashed Checks', icon: <FileCheck className="w-5 h-5" /> },
            { id: 'tax-forms', label: 'Tax Forms', icon: <FileCheck className="w-5 h-5" /> },
            { id: 'ledger', label: 'Ledger & Policy', icon: <DollarSign className="w-5 h-5" /> },
            { id: 'treasury-check', label: 'Treasury Checks', icon: <FileCheck className="w-5 h-5" /> },
            { id: 'eth-sales', label: 'ETH Sales', icon: <DollarSign className="w-5 h-5" /> },
            { id: 'gas-funding', label: 'Gas Funding', icon: <Fuel className="w-5 h-5" /> },
            { id: 'contracts', label: 'Contracts', icon: <FileCode className="w-5 h-5" /> },
            { id: 'market', label: 'BETH Market', icon: <ArrowRightLeft className="w-5 h-5" /> }
          ].map(tab => (
            <button
              key={tab.id}
              data-testid={`admin-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-purple-500 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        <SectionGuide key={activeTab} id={`admin-${activeTab}`} className="mb-6" />
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 text-purple-500 animate-spin mx-auto mb-4" />
            <p className="text-slate-400">Loading...</p>
          </div>
        ) : (
          <>
            {/* Clients Tab */}
            {activeTab === 'clients' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search clients..."
                      className="bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder-slate-500 w-64"
                    />
                  </div>
                  <button
                    onClick={() => setShowCredit(true)}
                    className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" /> Credit Client
                  </button>
                </div>

                <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-900">
                      <tr>
                        <th className="text-left text-slate-400 text-sm font-medium px-4 py-3">Client</th>
                        <th className="text-left text-slate-400 text-sm font-medium px-4 py-3">Wallet</th>
                        <th className="text-left text-slate-400 text-sm font-medium px-4 py-3">SSN / EIN</th>
                        <th className="text-right text-slate-400 text-sm font-medium px-4 py-3">Balance</th>
                        <th className="text-left text-slate-400 text-sm font-medium px-4 py-3">Fee Waiver</th>
                        <th className="text-left text-slate-400 text-sm font-medium px-4 py-3">Tier Fees</th>
                        <th className="text-left text-slate-400 text-sm font-medium px-4 py-3">Account</th>
                        <th className="text-left text-slate-400 text-sm font-medium px-4 py-3">Checks / Week</th>
                        <th className="text-right text-slate-400 text-sm font-medium px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {filteredClients.map((client, idx) => (
                        <tr key={idx} className="hover:bg-slate-700/50">
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-white font-medium">{client.first_name} {client.last_name}</p>
                              <p className="text-slate-400 text-sm">{client.email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <code className="text-emerald-400 text-sm">
                              {client.wallet_address?.slice(0, 12)}...{client.wallet_address?.slice(-8)}
                            </code>
                          </td>
                          <td className="px-4 py-3"><AdminTinCell client={client} token={token} /></td>
                          <td className="px-4 py-3 text-right">
                            <p className="text-white font-bold" data-testid={`admin-client-balance-${client.client_id}`}>{client.beth_balance?.toLocaleString() || 0} BETH</p>
                            <p className="text-slate-400 text-sm">${(client.beth_balance * (client.usd_per_beth ?? 1)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          </td>
                          <td className="px-4 py-3"><FeeWaiverSwitch client={client} token={token} /></td>
                          <td className="px-4 py-3"><TierWaiverSwitches client={client} token={token} /></td>
                          <td className="px-4 py-3">{!client.is_super_admin && <ActAsButton client={client} token={token} />}</td>
                          <td className="px-4 py-3"><WeeklyLimitCell client={client} token={token} /></td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => {
                                setCreditData({ ...creditData, client_id: client.client_id });
                                setShowCredit(true);
                              }}
                              data-testid={`admin-client-credit-${client.client_id}`}
                              className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded text-sm hover:bg-purple-500/30"
                            >
                              Credit
                            </button>
                            <button
                              onClick={() => {
                                setTransferData({ ...transferData, from_client_id: client.client_id });
                                setShowTransfer(true);
                              }}
                              data-testid={`admin-client-transfer-${client.client_id}`}
                              className="ml-2 px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded text-sm hover:bg-emerald-500/30"
                            >
                              Move BETH
                            </button>
                            <button
                              onClick={() => setToolsClient(client)}
                              data-testid={`admin-client-tools-${client.client_id}`}
                              className="ml-2 px-3 py-1 bg-amber-500/20 text-amber-300 rounded text-sm hover:bg-amber-500/30"
                            >
                              Manage
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <AdminActLog token={token} />
              </div>
            )}

            {/* Transactions Tab */}
            {activeTab === 'transactions' && (
              <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-900">
                    <tr>
                      <th className="text-left text-slate-400 text-sm font-medium px-4 py-3">Type</th>
                      <th className="text-left text-slate-400 text-sm font-medium px-4 py-3">TX Hash</th>
                      <th className="text-right text-slate-400 text-sm font-medium px-4 py-3">Amount</th>
                      <th className="text-left text-slate-400 text-sm font-medium px-4 py-3">Date</th>
                      <th className="text-right text-slate-400 text-sm font-medium px-4 py-3">Blockchain</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {transactions.map((tx, idx) => (
                      <tr key={idx} className="hover:bg-slate-700/50">
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            tx.type === 'admin_credit' ? 'bg-purple-500/20 text-purple-400' :
                            tx.type === 'purchase' ? 'bg-emerald-500/20 text-emerald-400' :
                            tx.type === 'transfer' ? 'bg-cyan-500/20 text-cyan-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {tx.type?.replace(/_/g, ' ').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <code className="text-slate-400 text-sm">
                            {tx.tx_hash?.slice(0, 16)}...
                          </code>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <p className="text-white font-bold">{tx.beth_amount} BETH</p>
                          <p className="text-slate-400 text-sm">${tx.usd_value}</p>
                        </td>
                        <td className="px-4 py-3 text-slate-400 text-sm">
                          {new Date(tx.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <ChainStatusCell tx={tx} token={token} onUpdated={fetchData} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Checks Tab */}
            {activeTab === 'checks' && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {checks.map((check, idx) => (
                  <div key={idx} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="text-emerald-400 font-mono text-sm">{check.check_number}</p>
                        <p className="text-white font-bold">{check.beth_amount} BETH</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        check.voided ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'
                      }`}>
                        {check.voided ? 'Voided' : check.status}
                      </span>
                    </div>
                    <p className="text-slate-400 text-sm mb-2">To: {check.payee_name}</p>
                    <div className="flex justify-between items-center">
                      <p className="text-slate-500 text-xs">
                        {new Date(check.created_at).toLocaleDateString()}
                      </p>
                      <a 
                        href={`/verify-check/${check.check_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`admin-check-verify-${check.check_number}`}
                        className="text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1"
                      >
                        Verify <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'credit-lines' && <AdminCreditLines token={token} />}
            {activeTab === 'redemptions' && <AdminRedemptions token={token} />}
            {activeTab === 'tax-forms' && <AdminTaxForms token={token} />}
            {activeTab === 'ledger' && <AdminLedger token={token} />}
            {activeTab === 'treasury-check' && <AdminTreasuryCheck token={token} />}
            {activeTab === 'eth-sales' && (
              <div className="space-y-6">
                <AdminMoonPayOrders token={token} />
                <AdminEthPurchases token={token} />
              </div>
            )}
            {activeTab === 'gas-funding' && <AdminSendEthCard token={token} />}
            {activeTab === 'market' && <BethMarketPanel token={token} />}
            {activeTab === 'contracts' && <div className="space-y-6"><IssuanceNetworkCard token={token} /><BridgeEthCard token={token} /><SubscriberGasCard token={token} /><WalletAlertsCard token={token} /><AdminContractDeployPanel token={token} /></div>}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && <AnalyticsDashboard token={token} />}

            {/* Videos Tab */}
            {activeTab === 'swaps' && <div className="space-y-6"><SwapReadinessCard token={token} /><AdminSwapPanel token={token} /></div>}
            {activeTab === 'desk' && <div className="space-y-6"><CreditCashoutAdmin token={token} /><AdminDeskPanel token={token} /></div>}
            {activeTab === 'earnings' && <EarningsDashboard token={token} />}
            {activeTab === 'hedge-fund' && <><AnnounceUpdateCard token={token} /><HedgeFundAdmin token={token} /></>}
            {activeTab === 'card-buys' && <CardBuysAdmin token={token} />}
            {activeTab === 'bank' && <AdminBankPanel token={token} />}
            {activeTab === 'partnerships' && <BankConnectivityHub token={token} />}
            {activeTab === 'videos' && (
              <>
                <FacebookSharePanel token={token} />
                <VideoGeneratorPanel token={token} />
              </>
            )}

            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && <AdminNetworkCard token={token} />}
            {activeTab === 'dashboard' && (
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 mb-6" data-testid="admin-restricted-sites">
                <h3 className="text-lg font-bold text-white mb-1">Restricted sites</h3>
                <p className="text-slate-400 text-sm mb-4">Only you can open these while signed in as BETHCOIN admin.</p>
                <div className="flex flex-wrap gap-3">
                  {[['BETH Finance', '/beth-finance'], ['Consumer Portal', '/beth-finance/login'], ['Legacy Admin', '/login'], ['Investors', '/investors']].map(([label, path]) => (
                    <button key={path} onClick={() => navigate(path)} data-testid={`restricted-link-${path.replace(/\//g, '') || 'root'}`}
                      className="px-4 py-2 bg-slate-900 border border-slate-600 hover:border-purple-500 text-white rounded-lg text-sm">{label}</button>
                  ))}
                </div>
              </div>
            )}
            {activeTab === 'dashboard' && (
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Network Info</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Network</span>
                      <span className="text-white font-medium" data-testid="admin-network-label">SEPOLIA/MAINNET</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">BETH Token Contract</span>
                      <a 
                        href={`https://sepolia.etherscan.io/token/${dashboard?.master_wallet?.beth_token_contract}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-400 hover:text-purple-300 flex items-center gap-1"
                      >
                        View <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Exchange Rate</span>
                      <span className="text-emerald-400 font-medium">{dashboard?.exchange_rate?.display}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => setShowCredit(true)}
                      className="w-full px-4 py-3 bg-purple-500/20 text-purple-400 rounded-lg font-medium hover:bg-purple-500/30 flex items-center gap-2"
                    >
                      <Plus className="w-5 h-5" /> Credit Any Client (Unlimited)
                    </button>
                    <button
                      onClick={() => { setTransferData({ ...transferData, transfer_type: 'external' }); setShowTransfer(true); }}
                      data-testid="quick-external-transfer-btn"
                      className="w-full px-4 py-3 bg-emerald-500/20 text-emerald-400 rounded-lg font-medium hover:bg-emerald-500/30 flex items-center gap-2"
                    >
                      <Send className="w-5 h-5" /> Transfer to External Wallet
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Credit Modal */}
      {showCredit && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-2">Credit Client</h3>
            <p className="text-purple-400 text-sm mb-6">Unlimited admin credit access</p>
            
            <form onSubmit={handleCredit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Select Client</label>
                <select
                  value={creditData.client_id}
                  onChange={(e) => setCreditData({ ...creditData, client_id: e.target.value })}
                  required
                  data-testid="credit-client-select"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-purple-500 outline-none"
                >
                  <option value="">Select a client...</option>
                  {clients.map((c, i) => (
                    <option key={i} value={c.client_id}>{c.email} - {c.first_name} {c.last_name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">BETH Amount</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={creditData.beth_amount}
                  onChange={(e) => setCreditData({ ...creditData, beth_amount: e.target.value })}
                  data-testid="credit-amount-input"
                  placeholder="Any amount (unlimited)"
                  required
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-purple-500 outline-none"
                />
                <p className="text-emerald-400 text-sm mt-1">
                  = ${(parseFloat(creditData.beth_amount || 0) * (clients.find(c => c.client_id === creditData.client_id)?.usd_per_beth ?? 0.01)).toFixed(2)} USD
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Memo</label>
                <input
                  type="text"
                  value={creditData.memo}
                  onChange={(e) => setCreditData({ ...creditData, memo: e.target.value })}
                  placeholder="Admin credit"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-purple-500 outline-none"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCredit(false)}
                  className="flex-1 border border-slate-600 text-white rounded-lg py-3 font-medium hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={crediting}
                  data-testid="credit-submit-btn"
                  className="flex-1 bg-purple-500 hover:bg-purple-600 text-white rounded-lg py-3 font-medium disabled:opacity-50"
                >
                  {crediting ? 'Processing...' : 'Credit BETH'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransfer && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-2">Transfer BETH</h3>
            <p className="text-purple-400 text-sm mb-6">Transfer to client or external wallet</p>
            
            <form onSubmit={handleTransfer} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">From</label>
                <select
                  value={transferData.from_client_id}
                  onChange={(e) => setTransferData({ ...transferData, from_client_id: e.target.value })}
                  data-testid="transfer-from-select"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-purple-500 outline-none"
                >
                  <option value="">Master wallet (unlimited)</option>
                  {clients.map((c, i) => (
                    <option key={i} value={c.client_id}>{c.email} ({c.beth_balance?.toLocaleString() || 0} BETH)</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Transfer Type</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="transfer_type"
                      value="internal"
                      data-testid="transfer-type-internal"
                      checked={transferData.transfer_type === 'internal'}
                      onChange={(e) => setTransferData({ ...transferData, transfer_type: e.target.value })}
                      className="text-purple-500"
                    />
                    <span className="text-slate-300">To Client</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="transfer_type"
                      value="external"
                      data-testid="transfer-type-external"
                      checked={transferData.transfer_type === 'external'}
                      onChange={(e) => setTransferData({ ...transferData, transfer_type: e.target.value })}
                      className="text-purple-500"
                    />
                    <span className="text-slate-300">External Wallet</span>
                  </label>
                </div>
              </div>

              {transferData.transfer_type === 'internal' ? (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Select Client</label>
                  <select
                    value={transferData.to_client_id}
                    onChange={(e) => setTransferData({ ...transferData, to_client_id: e.target.value })}
                    required
                    data-testid="transfer-to-client-select"
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-purple-500 outline-none"
                  >
                    <option value="">Select a client...</option>
                    {clients.filter(c => c.client_id !== transferData.from_client_id).map((c, i) => (
                      <option key={i} value={c.client_id}>{c.email}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">External Wallet Address</label>
                  <input
                    type="text"
                    value={transferData.to_external_address}
                    onChange={(e) => setTransferData({ ...transferData, to_external_address: e.target.value })}
                    data-testid="transfer-external-address-input"
                    placeholder="0x..."
                    required
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white font-mono focus:border-purple-500 outline-none"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">BETH Amount</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={transferData.beth_amount}
                  onChange={(e) => setTransferData({ ...transferData, beth_amount: e.target.value })}
                  data-testid="transfer-amount-input"
                  placeholder="Any amount (unlimited)"
                  required
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-purple-500 outline-none"
                />
              </div>

              <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                <p className="text-purple-300 text-sm flex items-center gap-2" data-testid="transfer-mode-note">
                  <CheckCircle className="w-4 h-4" />
                  {transferData.transfer_type === 'external'
                    ? 'Sends real BETH on Sepolia/Mainnet from the master wallet - visible on Etherscan'
                    : 'Recorded on the internal BETH ledger (no blockchain fee)'}
                </p>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowTransfer(false)}
                  className="flex-1 border border-slate-600 text-white rounded-lg py-3 font-medium hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={transferring}
                  data-testid="transfer-submit-btn"
                  className="flex-1 bg-purple-500 hover:bg-purple-600 text-white rounded-lg py-3 font-medium disabled:opacity-50"
                >
                  {transferring ? 'Processing...' : 'Transfer BETH'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <TxResultModal result={txResult} onClose={() => setTxResult(null)} />
      {toolsClient && (
        <AdminClientTools client={toolsClient} token={token} onClose={() => setToolsClient(null)}
          onDone={() => { setToolsClient(null); fetchData(); }} />
      )}
    </div>
  );
};

export default PASAdminDashboard;
